import { generateDataAttributes, generateMonth, getModifiers, validateDate } from './atoms/helper';
import { EffectReducerExec, EffectsMap } from 'use-effect-reducer';
import dayjs from 'dayjs';
import { StyleProp } from '@front10/base-elements';

export interface DateRange {
  start?: Date;
  end?: Date;
}
export interface StringDateRange {
  start?: string;
  end?: string;
}

export type BlackoutDaysRange = {
  start: string;
  end: string;
};

export type BlackoutDay = string | BlackoutDaysRange;

export type ModifierIndex =
  | 'today'
  | 'interactive'
  | 'hidden'
  | 'selected'
  | 'disabled'
  | 'past'
  | 'beginRange'
  | 'endRange'
  | 'beforemonth'
  | 'aftermonth'
  | 'preSelected'
  | 'blackout';

export type IModifiers = {
  [key in ModifierIndex]: boolean;
};

export type ModifiersColors = {
  [index: string]: any;
};

export interface ModifierCallable {
  (day: Date): boolean;
}

export type ModifierRange = {
  before: Date;
  after: Date;
};

export type DateModifier = {
  [key in ModifierIndex]: ModifierCallable | ModifierRange;
};

export type CommonDateProps = {
  /**
   * Show or hide the days of before and after month
   */
  showRemainingDays: boolean;
  /**
   * Allow setttings for calendar component
   */
  calendarProps: any;
  /**
   * Allowed char to enter in input
   */
  allowedCharacters: string;
  /**
   * Setting of input icon when popover is shown
   */
  rightIconOpen: any;
  /**
   * Setting of input
   */
  rightIcon: any;
  /**
   * Show separator
   */
  showSeparator: boolean;
  /**
   * day style props
   */
  /**
   * Modifiers colors style
   */
  modifiersColors: ModifiersColors;
  /**
   * renderDay function
   */
  renderDay: Function | null;
  /**
   * renderDay function
   */
  renderWeekdayElement: Function | null;
  /**
   * Settings for weekdays
   */
  weekdayItem: any;
  /**
   * Color of navigation buttons
   */
  navColor: string;
  /**
   * Settings for navigation
   */
  navigationItem: NavigationItem;
  /**
   * renderNavBarElement
   */
  renderNavbarElement: Function | null;
  /**
   * Patter to format date
   */
  format: string;
  /**
   * determine if show the calendar in One or Two Columns
   */
  showInOneColumn: boolean;
  popover: any;
  /**
   * Date format for the screen readers
   */
  inputDateScreenReader: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'D MMMM, YYYY' | 'MMMM D, YYYY';
  availableDays: number;
  captionItem: any;
  numberOfMonths: number;
  /**
   * Class when the value is invalid
   */
  invalidClass: string;

  onValueClass: string;

  dayItems: any;
  footerCheckboxComponent: Record<string, object>;
  footerContainerComponent: Record<string, object>;
  footerSelectComponent: Record<string, object>;
  footerInformationTextComponent: Record<string, object>;
  footerDoneButtonComponent: Record<string, object>;
  footerClearButtonComponent: Record<string, object>;

  blackoutDays: Array<BlackoutDay>;
  blackoutDaysFormat: string;
  errorLabelComponent?: any;
};

export type NavigationItem = any & {
  iconPrevProps: any;
  iconNextProps: any;
};

export type Focusable<T> = T & { focus: () => {} };

export interface DayData {
  date: Date;
  modifiers: IModifiers;
  classes: string;
  styles: any;
}

//reducers types

export type CommonCalendarState = {
  selectedComponent: 'start' | 'end';
  displayDate: Date;
  availableDays: number;
  months: Array<MonthHandler>;
  today: Date;
  currentDate: Set<number>;
  selectedRange: Set<number>;
  valid: boolean;
  focusSelectedDay: boolean;
  preSelected: Date | undefined;
  popoverOpen: boolean;
  currentMonth?: number;
  validateDone?: boolean;
};

export type StateSingleDatePicker = CommonCalendarState;
export type StateSingleDateRangePicker = CommonCalendarState;
type CommonActionsEffects =
  | { type: 'SET_PRESELECTED_DATE'; payload: Date | undefined; modifiers?: Partial<IModifiers> }
  | { type: 'SET_DISPLAY_DATE_ACTION'; payload: Date }
  | {
      type: 'SET_DATE_ACTION';
      payload: Set<number>;
      component: 'start' | 'end';
      close: boolean;
    }
  | { type: 'CHANGE_DATES'; payload?: Set<number> }
  | { type: 'CLEAR_ACTION' }
  | { type: 'CLOSE_POPOVER_ACTION' }
  | { type: 'CHANGE_CURRENT_MONTH'; payload: number }
  | { type: 'OPEN_POPOVER_ACTION' }
  | { type: 'CANCEL_ACTION' }
  | { type: 'SELECT_DAY_ACTION'; payload: Date; modifiers: IModifiers };

export type CommonCalendarActions =
  | CommonActionsEffects
  | { type: 'SET_FOCUS_SELECTED_DAY'; payload: boolean }
  | { type: 'SET_MONTHS'; payload: Array<MonthHandler> }
  | { type: 'SET_VALID'; payload: boolean }
  | { type: 'SET_DAY_FOCUS_BY_KEYBOARD'; payload: boolean }
  | { type: 'SET_POPOVER'; payload: { popoverOpen: boolean; focusSelectedDay: boolean } }
  | { type: 'VALIDATE_DONE' };

export type EventSingleDatePicker = CommonCalendarActions;
export type EventSingleDateRangePicker = CommonCalendarActions;

export type CommonCalendarEffects = CommonActionsEffects;

export type EffectSingleDatePicker = CommonCalendarEffects;

export type EffectSingleDateRangePicker = CommonCalendarEffects & {};
//TODO: Eliminate unused types
export type CalendarEffectsMap<
  S extends CommonCalendarState,
  A extends CommonCalendarActions,
  E extends CommonCalendarEffects
> = EffectsMap<S, A, E>;

export type CalendarReducerFn<
  S extends CommonCalendarState,
  A extends CommonCalendarActions,
  E extends CommonCalendarEffects
> = (state: S, action: A, exec: EffectReducerExec<S, A, E>) => S;

export class MonthHandler {
  private readonly month: Date;
  private readonly data: Array<Array<DayData>>;

  constructor(
    month: Date,
    modifiers: any,
    modifiersColors: any,
    showRemainingDays: boolean = false,
    locale: $TSFixMe = {}
  ) {
    this.month = month;

    this.data = generateMonth(
      month,
      showRemainingDays,
      (date) => {
        const dateModifiers = getModifiers(date, modifiers);
        const classes = generateDataAttributes(dateModifiers);
        return {
          date,
          classes,
          styles: modifiersColors,
          modifiers: dateModifiers
        };
      },
      locale
    );
  }
  public getMonth() {
    return this.month;
  }

  public getMonthData() {
    return this.data;
  }

  public firstAvailableDay(): Date | undefined {
    let available;

    for (let i = 0; i < this.data.length; i++) {
      const week = this.data[i];
      const days = week.filter((day) => {
        return validateDate(day.date, day.modifiers);
      });
      if (days.length > 0 && days[0].date) {
        available = days[0].date;
        break;
      }
    }
    return available;
  }

  public findDay(date: Date): DayData | null {
    let data = null;
    this.data.every((week) => {
      let result = true;

      week.every((dayData) => {
        if (dayjs(dayData.date).isSame(dayjs(date), 'day')) {
          data = { ...dayData };
          result = false;
        }
        return result;
      });
      return result;
    });
    return data;
  }
}

export interface InternalEvent {
  date: Date;
  label: string;
  styles: StyleProp;
}

export interface DateEvent {
  label: string;
  dates: string[];
  styles: StyleProp;
}
